/**
 * Парсер и утилиты для Suno timestamped-lyrics.
 *
 * Suno возвращает массив `alignedWords` — слова с точными таймингами (startS/endS).
 * Для karaoke-viewer нужно разбить их на строки (группировка по паузам и знакам).
 *
 * Источник данных в TaskStatusResponse:
 *   response_data = { poll_number, raw: { aligned_words: [...], ... } }
 *   (worker кладёт в task_events, фронт распаковывает)
 */
import type { TaskStatusResponse } from '../../../api/images'

export interface AlignedWord {
	word: string
	startS: number
	endS?: number
	success?: boolean
	p_align?: number
}

export interface LyricLine {
	text: string
	start: number
	end: number
	/** Слова строки с их таймингами — для пословной karaoke-подсветки.
	 *  Опционально: старые строки из localStorage-кэша его не содержат. */
	words?: AlignedWord[]
}

/** Группирует слова в строки по паузам ≥ pauseGapS и/или знакам препинания.
 *  Сохраняет сами слова (с таймингами) — нужно для пословной подсветки. */
export function groupWordsIntoLines(words: AlignedWord[], pauseGapS = 0.6): LyricLine[] {
	if (!words.length) return []
	const lines: LyricLine[] = []
	let current: AlignedWord[] = [words[0]]
	let lineStart = words[0].startS
	let prevEnd = words[0].endS ?? words[0].startS

	const flush = () => {
		lines.push({
			text: current.map((w) => w.word).join(' '),
			start: lineStart,
			end: prevEnd,
			words: current,
		})
	}

	for (let i = 1; i < words.length; i++) {
		const w = words[i]
		const gap = w.startS - prevEnd
		const prev = current[current.length - 1]?.word ?? ''
		const endsWithPunct = /[.!?]$/.test(prev)
		if (gap >= pauseGapS || endsWithPunct) {
			flush()
			current = []
			lineStart = w.startS
		}
		current.push(w)
		prevEnd = w.endS ?? w.startS
	}
	if (current.length) flush()
	return lines
}

/** Индекс активной строки: последняя, у которой start ≤ t. -1 — до первой. */
export function activeLineIndex(lines: LyricLine[], t: number): number {
	let idx = -1
	for (let i = 0; i < lines.length; i++) {
		if (lines[i].start <= t) idx = i
		else break
	}
	return idx
}

/** Индекс «поющегося» слова в строке: последнее, у которого startS ≤ t.
 *  -1 — если ещё не дошли до строки или у строки нет пословных таймингов. */
export function activeWordIndex(line: LyricLine | undefined, t: number): number {
	const words = line?.words
	if (!words || !words.length) return -1
	let idx = -1
	for (let i = 0; i < words.length; i++) {
		if (words[i].startS <= t) idx = i
		else break
	}
	return idx
}

/** Вытаскивает aligned_words из разных shape'ов response_data. */
export function extractAlignedWords(
	data: unknown,
): AlignedWord[] | null {
	if (!data || typeof data !== 'object') return null
	// Worker кладёт {poll_number, raw: {...}}. Развернём.
	const wrapped = data as { raw?: unknown }
	const payload = wrapped.raw && typeof wrapped.raw === 'object' ? wrapped.raw : data
	const p = payload as {
		aligned_words?: AlignedWord[]
		alignedWords?: AlignedWord[]
	}
	const words = p.aligned_words || p.alignedWords
	if (Array.isArray(words) && words.length > 0 && typeof words[0].startS === 'number') {
		return words
	}
	return null
}

export function extractLinesFromTask(s: TaskStatusResponse): LyricLine[] | null {
	const words = extractAlignedWords(s.response_data)
	if (!words) return null
	return groupWordsIntoLines(words)
}

export function formatTime(s: number): string {
	if (!Number.isFinite(s) || s < 0) return '0:00'
	const mm = Math.floor(s / 60)
	const ss = Math.floor(s % 60)
	return `${mm}:${ss.toString().padStart(2, '0')}`
}
